import * as React from "react"
import { object, string, array } from "yup"
import { Grid, TextField } from "@mui/material"
import { DrawerSeeker, AutocompleteAjaxSeeker } from "@ui-seeker/core"
import { useValidatorSeeker, useAppToast } from "@ui-seeker/app"
import { isEmpty } from '@ui-seeker/utils'

import { BACKEND_APIS } from "@constants"
import { skuGroupDrawerFilters } from '../@signals'
import useSkuGroupDrawerForm from './@hooks/useForm'


export default function SkuGroupDrawer() {
    const { showToast } = useAppToast()
    const { name, skuIDs, userIDs, brandFilter, customerFilter, handleSubmit, drawerTitle } = useSkuGroupDrawerForm()

    const isCreate = skuGroupDrawerFilters.value?.mode === 'create'

    const schema = object({
        name: string().required('Group name is required'),
        skuIDs: array().min(1, 'At least one SKU is required'),
        ...(isCreate && { userIDs: array().min(1, 'At least one user is required') }),
    })

    const validatorData = useValidatorSeeker({
        name: name.value,
        skuIDs: skuIDs.value,
        ...(isCreate && { userIDs: userIDs.value }),
    }, schema)

    const isOpen = !isEmpty(skuGroupDrawerFilters.value)

    const drawerProps = {
        title: drawerTitle,
        isOpen,
        controller: () => { skuGroupDrawerFilters.value = {} },
        successProps: {
            onClick: isEmpty(validatorData.errors)
                ? handleSubmit
                : () => showToast.warning('Please ensure all mandatory fields are completed'),
        }
    }

    return <DrawerSeeker {...drawerProps}>
        <Grid container spacing={2}>

            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Group Name"
                    size="small"
                    value={name.value}
                    onChange={e => { name.value = e.target.value }}
                    sx={{ mt: 3, mb: 1 }}
                    helperText={!!isOpen && validatorData.errors?.name}
                    error={!!isOpen && !!validatorData.errors?.name}
                />
            </Grid>

            {/* Customer — editable on create, read-only on edit */}
            <Grid item xs={12}>
                {skuGroupDrawerFilters.value?.mode === 'edit'
                    ? <TextField
                        fullWidth
                        label="Customer"
                        size="small"
                        value={skuGroupDrawerFilters.value.customerName || ''}
                        disabled
                        sx={{ mb: 1 }}
                    />
                    : isOpen && <AutocompleteAjaxSeeker
                        urlAjax={`${BACKEND_APIS.MANAGEMENT}/keywords/customer-options`}
                        label="Customer"
                        placeholder="Select a customer..."
                        value={customerFilter.value}
                        onChange={e => {
                            customerFilter.value = e.target.value
                            brandFilter.value = []
                            skuIDs.value = []
                            userIDs.value = []
                        }}
                        sx={{ mb: 1 }}
                    />
                }
            </Grid>

            {/* Brand filter + SKUs — only shown after a customer is selected */}
            {isOpen && customerFilter.value && <>
                <Grid item xs={12}>
                    <AutocompleteAjaxSeeker
                        urlAjax={`${BACKEND_APIS.MANAGEMENT}/sku-group/brand-options`}
                        filters={{ customerID: customerFilter.value }}
                        label="Filter by Brand"
                        placeholder="Select brands to filter SKUs..."
                        multiple
                        value={brandFilter.value}
                        onChange={e => { brandFilter.value = e.target.value }}
                        sx={{ mb: 1 }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <AutocompleteAjaxSeeker
                        urlAjax={`${BACKEND_APIS.MANAGEMENT}/sku-group/sku-options`}
                        filters={{ customerID: customerFilter.value, brandIDs: brandFilter.value }}
                        label="SKUs"
                        placeholder="Search SKUs..."
                        multiple
                        value={skuIDs.value}
                        onChange={e => { skuIDs.value = e.target.value }}
                        sx={{ mb: 1 }}
                        error={validatorData.errors?.skuIDs}
                    />
                </Grid>

                {isCreate && <Grid item xs={12}>
                    <AutocompleteAjaxSeeker
                        urlAjax={`${BACKEND_APIS.MANAGEMENT}/sku-group/user-options`}
                        filters={{ customerID: customerFilter.value }}
                        label="Users"
                        placeholder="Select users..."
                        multiple
                        value={userIDs.value}
                        onChange={e => { userIDs.value = e.target.value }}
                        sx={{ mb: 1 }}
                        error={validatorData.errors?.userIDs}
                    />
                </Grid>}
            </>}

        </Grid>
    </DrawerSeeker>
}
